import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cards as TrelloIcon, ArrowSquareOut } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable "Export to Trello" button — mirrors NotionExportButton.
 */
export default function TrelloExportButton({ shape_type, source_id, testId = 'btn-export-trello', variant = 'pill', className = '' }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [configured, setConfigured] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/trello/settings`);
        if (alive) setConfigured(!!data?.configured);
      } catch {
        if (alive) setConfigured(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleClick = useCallback(async () => {
    if (!source_id) {
      toast.error(t('exp.nothing_to_export'));
      return;
    }
    if (configured === false) {
      toast.message(t('exp.configure_first', { service: 'Trello' }));
      navigate('/settings#trello');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/trello/export`, { shape_type, source_id });
      toast.success(t('exp.exported', { service: 'Trello' }), {
        description: `${data.title}${data.checklist_items ? ` · ${t('exp.action_items', { count: data.checklist_items })}` : ''}`,
        action: data.trello_url ? {
          label: t('exp.open_card'),
          onClick: () => window.open(data.trello_url, '_blank', 'noopener,noreferrer'),
        } : undefined,
        duration: 8000,
      });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 412) {
        toast.message(t('exp.configure_first', { service: 'Trello' }));
        navigate('/settings#trello');
      } else {
        toast.error(detail || t('exp.export_fail', { service: 'Trello' }));
      }
    } finally {
      setBusy(false);
    }
  }, [configured, navigate, shape_type, source_id, t]);

  const isLoadingProbe = configured === null;
  const base = variant === 'ghost'
    ? 'inline-flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs uppercase tracking-wider transition disabled:opacity-50'
    : 'inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs font-semibold transition disabled:opacity-50';

  return (
    <button
      type="button"
      data-testid={testId}
      data-configured={configured === true ? 'true' : 'false'}
      onClick={handleClick}
      disabled={busy || isLoadingProbe}
      title={configured === false ? t('exp.tooltip_configure', { service: 'Trello' }) : t('exp.tooltip_export', { service: 'Trello' })}
      className={`${base} ${className}`}
    >
      {busy ? (
        <>
          <ArrowSquareOut size={13} weight="bold" className="animate-pulse" /> {t('exp.exporting')}
        </>
      ) : (
        <>
          <TrelloIcon size={13} weight="fill" /> Trello
        </>
      )}
    </button>
  );
}
